import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  ChevronLeft, User, Mail, MapPin, Calendar, Edit2, Camera, LogOut, 
  Save, X, Heart, Loader2, GraduationCap, Award, BookOpen, 
  AlertTriangle, XCircle, Eye, EyeOff, FileCheck, Upload, Clock, ShieldCheck, Coins, KeyRound, ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PDetail = ({ l, v }) => (
  <div>
    <span className="text-xs text-stone-400">{l}</span>
    <p className="text-sm font-medium text-stone-700">{v}</p>
  </div>
);

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token, logout, updateUser, refreshUser, isAuthenticated, loading } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [photoPointsAwarded, setPhotoPointsAwarded] = useState(false);
  const fileInputRef = useRef(null);
  const docFileInputRef = useRef(null);
  const [docStatus, setDocStatus] = useState(null);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [fullProfile, setFullProfile] = useState(null);

  // 비밀번호 변경 상태
  const [showPwForm, setShowPwForm] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwShow, setPwShow] = useState({ current: false, next: false, confirm: false });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    setPwSuccess("");
    if (pwForm.next.length < 6) {
      setPwError("새 비밀번호는 6자 이상이어야 합니다.");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwError("새 비밀번호 확인이 일치하지 않습니다.");
      return;
    }
    if (pwForm.next === pwForm.current) {
      setPwError("새 비밀번호가 기존 비밀번호와 동일합니다.");
      return;
    }
    setPwLoading(true);
    try {
      const tk = token || (typeof localStorage !== "undefined" ? localStorage.getItem("liztv_token") : null);
      if (!tk) {
        setPwError("로그인이 만료되었어요. 다시 로그인한 뒤 시도해주세요.");
        setPwLoading(false);
        return;
      }
      const res = await safeFetch(`${API_URL}/api/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.next }),
      });
      let data = {};
      try { data = (await res.json()) || {}; } catch (_e) { data = {}; }
      if (!res.ok) {
        if (res.status === 401) throw new Error("로그인이 만료되었어요. 다시 로그인한 뒤 시도해주세요.");
        if ([502, 503, 504].includes(res.status)) throw new Error("일시적인 서버 오류예요. 잠시 후 다시 시도해주세요.");
        throw new Error(data.detail || data.message || `비밀번호 변경에 실패했습니다. (오류 ${res.status})`);
      }
      setPwSuccess("비밀번호가 변경되었습니다.");
      setPwForm({ current: "", next: "", confirm: "" });
      setTimeout(() => { setShowPwForm(false); setPwSuccess(""); }, 1800);
    } catch (err) {
      setPwError(err.message || "네트워크 연결이 불안정해요. 잠시 후 다시 시도해주세요.");
    } finally {
      setPwLoading(false);
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError("JPG, PNG, GIF, WEBP 형식만 지원합니다.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("파일 크기는 5MB 이하만 가능합니다.");
      return;
    }

    setIsUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await safeFetch(`${API_URL}/api/auth/upload-profile-image`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData,
      });

      // Read body ONCE as text, then safely parse — avoids "body stream already read"
      const rawText = await response.text();
      let data = {};
      if (rawText) {
        try { data = JSON.parse(rawText); } catch { data = { detail: rawText }; }
      }
      if (!response.ok) {
        throw new Error(data.detail || `업로드 실패 (HTTP ${response.status})`);
      }

      if (data.points_awarded > 0) {
        setPhotoPointsAwarded(true);
        setTimeout(() => setPhotoPointsAwarded(false), 4000);
      }
      if (refreshUser) await refreshUser();
    } catch (err) {
      console.error("[ProfilePhotoUpload] error:", err);
      setError(err.message || "사진 업로드 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const [creditsData, setCreditsData] = useState({
    total_credits: 0,
    completed_count: 0,
    credits_by_curriculum: {},
    history: []
  });
  
  const [formData, setFormData] = useState({
    username: "",
    gender: "",
    birth_year: "",
    location: "",
    introduction: "",
  });

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      navigate("/");
    } else if (refreshUser) {
      refreshUser();
    }
  }, [isAuthenticated, navigate, refreshUser, loading]);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || "",
        gender: user.gender || "",
        birth_year: user.birth_year || "",
        location: user.location || "",
        introduction: user.introduction || "",
        name: user.name || "",
        job: user.job || "",
        personality: user.personality || [],
        hobbies: user.hobbies || "",
      });
    }
  }, [user]);

  // Fetch user credits
  useEffect(() => {
    const fetchFullProfile = async () => {
      if (!token) return;
      try {
        const res = await safeFetch(`${API_URL}/api/auth/me/full`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setFullProfile(await res.json());
      } catch {}
    };
    fetchFullProfile();

    const fetchCredits = async () => {
      if (!token) return;
      try {
        const response = await safeFetch(`${API_URL}/api/user/credits`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCreditsData(data);
        }
      } catch (err) {
        console.error("Failed to fetch credits:", err);
      }
    };
    fetchCredits();
  }, [token]);

  // Fetch document verification status
  useEffect(() => {
    const fetchDocStatus = async () => {
      if (!token) return;
      try {
        const res = await safeFetch(`${API_URL}/api/auth/document-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDocStatus(data);
        }
      } catch (err) { console.error("Failed to fetch doc status:", err); }
    };
    fetchDocStatus();
  }, [token]);

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) { setError("JPG, PNG, GIF, WEBP, PDF 형식만 지원합니다."); return; }
    if (file.size > 10 * 1024 * 1024) { setError("파일 크기는 10MB 이하만 가능합니다."); return; }
    setIsUploadingDoc(true); setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", "marriage_certificate");
      const res = await safeFetch(`${API_URL}/api/auth/upload-document`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "업로드 실패");
      setDocStatus({ status: "pending", doc_type: "marriage_certificate" });
    } catch (err) { setError(err.message); }
    finally { setIsUploadingDoc(false); if (docFileInputRef.current) docFileInputRef.current.value = ""; }
  };

  const handleSave = async () => {
    setError("");
    setIsLoading(true);

    try {
      const response = await safeFetch(`${API_URL}/api/auth/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          birth_year: parseInt(formData.birth_year),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "프로필 업데이트에 실패했습니다.");
      }

      updateUser(data);
      setIsEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");

  const handleCancelMembership = async (deleteData = false) => {
    setIsCancelling(true);
    try {
      const response = await safeFetch(`${API_URL}/api/auth/cancel-membership`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_data: deleteData }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "멤버십 캔슬에 실패했습니다.");
      }
      setShowCancelModal(false);
      setConfirmAction(null);
      logout();
      navigate("/");
    } catch (err) {
      setError(err.message);
      setShowCancelModal(false);
      setConfirmAction(null);
    } finally {
      setIsCancelling(false);
    }
  };

  const requestConfirm = (deleteData) => {
    if (deleteData) {
      setConfirmMessage("모든 데이터가 영구 삭제되며 복구할 수 없습니다. 정말 진행하시겠습니까?");
    } else {
      setConfirmMessage("회원 정보보기 목록에서 비공개 처리됩니다. 진행하시겠습니까?");
    }
    setConfirmAction(() => () => handleCancelMembership(deleteData));
  };

  const [listingBusy, setListingBusy] = useState(false);

  const handleToggleListing = async (nextHidden) => {
    if (listingBusy) return;
    setListingBusy(true);
    try {
      const endpoint = nextHidden ? "hide-listing" : "restore-listing";
      const response = await safeFetch(`${API_URL}/api/auth/${endpoint}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "처리에 실패했습니다.");
      }
      updateUser({ ...user, hidden_from_listing: nextHidden });
    } catch (err) {
      setError(err.message);
    } finally {
      setListingBusy(false);
    }
  };

  const handleRestoreListing = async () => {
    try {
      const response = await safeFetch(`${API_URL}/api/auth/restore-listing`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "복귀에 실패했습니다.");
      }
      window.location.reload();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
      </div>
    );
  }

  const age = user.birth_year ? new Date().getFullYear() - user.birth_year : null;

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
          {/* Cover & Avatar */}
          <div className="relative h-32 bg-gradient-to-r from-burgundy-500 to-burgundy-700">
            <div className="absolute -bottom-12 left-8 flex items-end gap-4">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-white border-4 border-white overflow-hidden shadow-lg">
                  {user.profile_image ? (
                    <img 
                      src={user.profile_image} 
                      alt={user.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-stone-200 to-stone-300 flex items-center justify-center">
                      <User className="w-10 h-10 text-stone-400" />
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleProfileImageUpload}
                  className="hidden"
                  data-testid="profile-image-input"
                />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-burgundy-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-burgundy-600 transition-colors disabled:opacity-50"
                  data-testid="profile-image-upload-btn"
                >
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </button>
              </div>
              {/* 등급 배지 — vip / 프리미엄 / 정회원 / 일반 구분 표기 */}
              <div className={`mb-1 flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-sm text-xs font-semibold ${
                user.membership_type === "vip" 
                  ? "bg-amber-100 text-amber-700 border border-amber-300" 
                  : user.membership_type === "premium"
                    ? "bg-rose-100 text-rose-700 border border-rose-300"
                    : (user.membership_type === "regular" || user.membership_type === "standard")
                      ? "bg-blue-100 text-blue-700 border border-blue-300" 
                      : "bg-stone-100 text-stone-600 border border-stone-300"
              }`} data-testid="membership-badge">
                {user.membership_type === "vip" && <Award className="w-3.5 h-3.5" />}
                {(user.membership_type === "regular" || user.membership_type === "standard" || user.membership_type === "premium") && <BookOpen className="w-3.5 h-3.5" />}
                {(!user.membership_type || user.membership_type === "basic") && <User className="w-3.5 h-3.5" />}
                {user.membership_type === "vip" ? "VIP" : user.membership_type === "premium" ? "프리미엄" : (user.membership_type === "regular" || user.membership_type === "standard") ? "정회원" : "일반회원"}
              </div>
              {/* 서류인증 배지 */}
              {user.document_verified && (
                <div className="mb-1 flex items-center gap-1 px-2.5 py-1.5 rounded-full shadow-sm text-xs font-semibold bg-cyan-100 text-cyan-700 border border-cyan-300" data-testid="doc-verified-badge">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  인증
                </div>
              )}
            </div>
            {/* 사진 업로드 포인트 안내 */}
            {photoPointsAwarded && (
              <div className="absolute top-2 right-8 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg animate-bounce" data-testid="photo-points-toast">
                +5,000P 획득!
              </div>
            )}
            {!user.profile_image && (
              <div className="absolute bottom-2 left-8 right-8 text-center" data-testid="photo-upload-guide">
                <span className="inline-flex items-center gap-1 text-[10px] bg-white/90 backdrop-blur-sm text-emerald-700 font-semibold px-2.5 py-1 rounded-full shadow-sm">
                  <Coins className="w-3 h-3" /> 사진 등록 시 5,000P 지급
                  {["regular", "premium", "vip"].includes(user.membership_type) && (
                    <span className="text-red-500 ml-1">필수</span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="pt-16 px-8 pb-8">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Header with Edit Button */}
            <div className="flex items-start justify-between mb-6">
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="text-2xl font-bold text-stone-800 bg-stone-50 px-3 py-1 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                    data-testid="edit-username"
                  />
                ) : (
                  <h2 className="text-2xl font-bold text-stone-800">{user.username}</h2>
                )}
                <p className="text-stone-500 flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </p>
                <a
                  href={`/timeline/${user.id}`}
                  className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-burgundy-700 hover:text-burgundy-800 group"
                  data-testid="profile-timeline-link"
                >
                  <span>내 감성 타임라인 보기</span>
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </a>
              </div>
              
              {isEditing ? (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        username: user.username,
                        gender: user.gender,
                        birth_year: user.birth_year,
                        location: user.location,
                        introduction: user.introduction,
                      });
                    }}
                    className="border-stone-300 text-stone-600"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="btn-primary flex items-center gap-2"
                    data-testid="save-profile-btn"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        저장
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                  className="border-stone-300 text-stone-600 flex items-center gap-2"
                  data-testid="edit-profile-btn"
                >
                  <Edit2 className="w-4 h-4" />
                  수정
                </Button>
              )}
            </div>

            {/* Profile Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Gender */}
              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  user.gender === "male" ? "bg-blue-100" : "bg-rose-100"
                }`}>
                  <Heart className={`w-5 h-5 ${
                    user.gender === "male" ? "text-blue-500" : "text-rose-500"
                  }`} />
                </div>
                <div>
                  <p className="text-sm text-stone-500">성별</p>
                  {isEditing ? (
                    <div className="flex gap-2 mt-1">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, gender: "male" })}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          formData.gender === "male"
                            ? "bg-blue-100 text-blue-600"
                            : "bg-stone-200 text-stone-500"
                        }`}
                      >
                        남성
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, gender: "female" })}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          formData.gender === "female"
                            ? "bg-rose-100 text-rose-600"
                            : "bg-stone-200 text-stone-500"
                        }`}
                      >
                        여성
                      </button>
                    </div>
                  ) : (
                    <p className="font-medium text-stone-800">
                      {user.gender === "male" ? "남성" : "여성"}
                    </p>
                  )}
                </div>
              </div>

              {/* Age */}
              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">나이</p>
                  {isEditing ? (
                    <input
                      type="number"
                      value={formData.birth_year}
                      onChange={(e) => setFormData({ ...formData, birth_year: e.target.value })}
                      className="font-medium text-stone-800 bg-white px-2 py-1 rounded border border-stone-200 w-24 mt-1"
                      placeholder="출생년도"
                      data-testid="edit-birth-year"
                    />
                  ) : (
                    <p className="font-medium text-stone-800">
                      {age}세 ({user.birth_year}년생)
                    </p>
                  )}
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl md:col-span-2">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-stone-500">거주지역</p>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="font-medium text-stone-800 bg-white px-2 py-1 rounded border border-stone-200 w-full mt-1"
                      placeholder="거주지역"
                      data-testid="edit-location"
                    />
                  ) : (
                    <p className="font-medium text-stone-800">{user.location}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Extra Profile Fields - All Members */}
            <div className="mt-6 space-y-4">
                {/* Name */}
                <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-violet-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-stone-500">이름</p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="font-medium text-stone-800 bg-white px-2 py-1 rounded border border-stone-200 w-full mt-1"
                        placeholder="이름을 입력하세요"
                        data-testid="edit-name"
                      />
                    ) : (
                      <p className="font-medium text-stone-800">{user.name || "미입력"}</p>
                    )}
                  </div>
                </div>

                {/* Job */}
                <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <Award className="w-5 h-5 text-teal-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-stone-500">직업</p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.job}
                        onChange={(e) => setFormData({ ...formData, job: e.target.value })}
                        className="font-medium text-stone-800 bg-white px-2 py-1 rounded border border-stone-200 w-full mt-1"
                        placeholder="직업을 입력하세요"
                        data-testid="edit-job"
                      />
                    ) : (
                      <p className="font-medium text-stone-800">{user.job || "미입력"}</p>
                    )}
                  </div>
                </div>

                {/* Personality - 5 choices */}
                <div className="p-4 bg-stone-50 rounded-xl">
                  <p className="text-sm text-stone-500 mb-3">성격 선택 (최대 5개)</p>
                  {isEditing ? (
                    <div className="flex flex-wrap gap-2">
                  {["유머러스", "따뜻한", "차분한", "활발한", "낙천적",
                    "신중한", "배려심있는", "독립적인", "사교적인", "내성적인",
                    "열정적인", "이성적인", "감성적인", "책임감있는", "도전적인",
                    "로맨틱한", "현실적인", "긍정적인", "섬세한", "자유로운",
                    "다정한", "진지한", "솔직한", "여유로운", "호기심많은"].map((trait) => (
                        <button
                          key={trait}
                          type="button"
                          onClick={() => {
                            const current = formData.personality || [];
                            if (current.includes(trait)) {
                              setFormData({ ...formData, personality: current.filter(t => t !== trait) });
                            } else if (current.length < 5) {
                              setFormData({ ...formData, personality: [...current, trait] });
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            (formData.personality || []).includes(trait)
                              ? "bg-burgundy-500 text-white"
                              : "bg-white text-stone-600 border border-stone-200 hover:border-burgundy-300"
                          }`}
                          data-testid={`personality-${trait}`}
                        >
                          {trait}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(user.personality && user.personality.length > 0) ? (
                        user.personality.map((trait) => (
                          <span key={trait} className="px-3 py-1.5 rounded-full text-xs font-medium bg-burgundy-100 text-burgundy-700">
                            {trait}
                          </span>
                        ))
                      ) : (
                        <p className="text-stone-400 text-sm">미선택</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Hobbies */}
                <div className="p-4 bg-stone-50 rounded-xl">
                  <p className="text-sm text-stone-500 mb-2">취미 & 관심사</p>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.hobbies}
                      onChange={(e) => setFormData({ ...formData, hobbies: e.target.value })}
                      className="font-medium text-stone-800 bg-white px-2 py-1 rounded border border-stone-200 w-full"
                      placeholder="예: 여행, 독서, 요리, 영화감상"
                      data-testid="edit-hobbies"
                    />
                  ) : (
                    <p className="font-medium text-stone-800">{user.hobbies || "미입력"}</p>
                  )}
              </div>
            </div>

            {/* 상세 자기소개서 작성/수정 진입 — 모든 회원(무료회원 포함) 가능 */}
            {!isEditing && (
              <button
                onClick={() => navigate("/profile/intro")}
                className="mt-6 w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-burgundy-200 bg-gradient-to-r from-burgundy-50 to-rose-50 hover:from-burgundy-100 hover:to-rose-100 transition-colors text-left"
                data-testid="write-self-intro-btn"
              >
                <span className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-burgundy-500 shrink-0">
                    <BookOpen className="w-4 h-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-stone-800">
                      상세 자기소개서 {fullProfile && (fullProfile.height || fullProfile.education || fullProfile.religion || fullProfile.marital_status || fullProfile.mbti || fullProfile.ideal_type || fullProfile.introduction) ? "수정하기" : "작성하기"}
                    </span>
                    <span className="block text-xs text-stone-500 mt-0.5">외모·성향·가치관·이상형까지 상세 정보를 작성할 수 있어요 (무료회원도 가능)</span>
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-burgundy-500 shrink-0" />
              </button>
            )}

            {/* 추가 프로필 정보 (자기소개서) */}
            {fullProfile && (fullProfile.height || fullProfile.education || fullProfile.religion || fullProfile.marital_status || fullProfile.mbti || fullProfile.ideal_type || fullProfile.introduction) && (
              <div className="mt-6 space-y-4">
                <h3 className="text-sm font-bold text-burgundy-600 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  자기소개서 상세
                </h3>
                {(fullProfile.height || fullProfile.weight || fullProfile.appearance_style || fullProfile.mbti || fullProfile.blood_type) && (
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-xs font-semibold text-stone-400 mb-2">외모/신체 정보</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {fullProfile.height && <PDetail l="키" v={fullProfile.height} />}
                      {fullProfile.weight && <PDetail l="몸무게" v={fullProfile.weight} />}
                      {fullProfile.appearance_style && <PDetail l="외모스타일" v={fullProfile.appearance_style} />}
                      {fullProfile.mbti && <PDetail l="MBTI" v={fullProfile.mbti} />}
                      {fullProfile.blood_type && <PDetail l="혈액형" v={fullProfile.blood_type} />}
                      {fullProfile.charm_point && <PDetail l="매력포인트" v={fullProfile.charm_point} />}
                    </div>
                  </div>
                )}
                {(fullProfile.marital_status || fullProfile.has_children) && (
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-xs font-semibold text-stone-400 mb-2">결혼/자녀</p>
                    <div className="grid grid-cols-2 gap-2">
                      {fullProfile.marital_status && <PDetail l="싱글여부" v={fullProfile.marital_status} />}
                      {fullProfile.has_children && <PDetail l="자녀" v={fullProfile.has_children === "yes" ? "있음" : "없음"} />}
                    </div>
                  </div>
                )}
                {(fullProfile.education || fullProfile.school_name || fullProfile.company_name || fullProfile.position) && (
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-xs font-semibold text-stone-400 mb-2">학력/직업</p>
                    <div className="grid grid-cols-2 gap-2">
                      {fullProfile.education && <PDetail l="최종학력" v={fullProfile.education} />}
                      {fullProfile.school_name && <PDetail l="학교명" v={fullProfile.school_name} />}
                      {fullProfile.company_name && <PDetail l="회사명" v={fullProfile.company_name} />}
                      {fullProfile.position && <PDetail l="직위" v={fullProfile.position} />}
                    </div>
                  </div>
                )}
                {(fullProfile.religion || fullProfile.drinking || fullProfile.smoking) && (
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-xs font-semibold text-stone-400 mb-2">성격/생활</p>
                    <div className="grid grid-cols-2 gap-2">
                      {fullProfile.religion && <PDetail l="종교" v={fullProfile.religion} />}
                      {fullProfile.drinking && <PDetail l="음주" v={fullProfile.drinking} />}
                      {fullProfile.drinking_amount && <PDetail l="주량" v={fullProfile.drinking_amount} />}
                      {fullProfile.smoking && <PDetail l="흡연" v={fullProfile.smoking} />}
                    </div>
                    {fullProfile.strengths_weaknesses && <div className="mt-2"><PDetail l="성격/장단점" v={fullProfile.strengths_weaknesses} /></div>}
                  </div>
                )}
                {(fullProfile.leisure_activities || fullProfile.favorite_food || fullProfile.stress_relief) && (
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-xs font-semibold text-stone-400 mb-2">취미/관심사</p>
                    <div className="grid grid-cols-2 gap-2">
                      {fullProfile.leisure_activities && <PDetail l="여가활동" v={fullProfile.leisure_activities} />}
                      {fullProfile.favorite_food && <PDetail l="좋아하는 음식" v={fullProfile.favorite_food} />}
                      {fullProfile.stress_relief && <PDetail l="스트레스 해소법" v={fullProfile.stress_relief} />}
                    </div>
                  </div>
                )}
                {(fullProfile.life_motto || fullProfile.favorite_movie || fullProfile.favorite_book || fullProfile.important_values || fullProfile.future_vision) && (
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-xs font-semibold text-stone-400 mb-2">가치관/인생관</p>
                    <div className="space-y-1.5">
                      {fullProfile.life_motto && <PDetail l="인생좌우명" v={fullProfile.life_motto} />}
                      {fullProfile.favorite_movie && <PDetail l="인생영화" v={fullProfile.favorite_movie} />}
                      {fullProfile.favorite_book && <PDetail l="인생 책" v={fullProfile.favorite_book} />}
                      {fullProfile.happy_moments && <PDetail l="행복한 순간" v={fullProfile.happy_moments} />}
                      {fullProfile.current_interests && <PDetail l="현재 관심사" v={fullProfile.current_interests} />}
                      {fullProfile.proud_achievement && <PDetail l="보람된 성취" v={fullProfile.proud_achievement} />}
                      {fullProfile.important_values && <PDetail l="중요한 가치" v={fullProfile.important_values} />}
                      {fullProfile.future_vision && <PDetail l="10년 뒤 모습" v={fullProfile.future_vision} />}
                    </div>
                  </div>
                )}
                {(fullProfile.ideal_type || fullProfile.marriage_goal) && (
                  <div className="p-4 bg-burgundy-50 rounded-xl border border-burgundy-100">
                    <p className="text-xs font-semibold text-burgundy-400 mb-2">이상형/결혼관</p>
                    {fullProfile.ideal_type && <PDetail l="이상형" v={fullProfile.ideal_type} />}
                    {fullProfile.marriage_goal && <PDetail l="결혼 목표" v={fullProfile.marriage_goal} />}
                  </div>
                )}
                {fullProfile.introduction && (
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-xs font-semibold text-stone-400 mb-2">자기소개</p>
                    <p className="text-sm text-stone-700 whitespace-pre-line leading-relaxed">{fullProfile.introduction}</p>
                  </div>
                )}
              </div>
            )}

            {/* Document Verification Section */}
            <div className="mt-6 p-5 bg-stone-50 rounded-xl border border-stone-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                  <FileCheck className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="font-semibold text-stone-800 text-sm">서류인증</p>
                  <p className="text-xs text-stone-400">
                    혼인관계증명서를 제출하면 10,000P가 지급됩니다
                    {["regular", "premium", "vip"].includes(user.membership_type) && (
                      <span className="text-red-500 font-semibold ml-1">(정회원 이상 필수)</span>
                    )}
                  </p>
                  <p className="text-[10px] text-emerald-600 mt-0.5">서류는 암호화 저장되며, 확인 후 즉시 삭제됩니다</p>
                </div>
              </div>

              {(!docStatus || docStatus.status === "none") && (
                <div>
                  <input type="file" ref={docFileInputRef} accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleDocumentUpload} className="hidden" data-testid="doc-file-input" />
                  <button onClick={() => docFileInputRef.current?.click()} disabled={isUploadingDoc}
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-stone-300 rounded-xl text-stone-500 hover:border-cyan-400 hover:text-cyan-600 hover:bg-cyan-50 transition-all disabled:opacity-50"
                    data-testid="doc-upload-btn">
                    {isUploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {isUploadingDoc ? "업로드 중..." : "혼인관계증명서 업로드"}
                  </button>
                  <p className="text-xs text-stone-400 mt-2 text-center">JPG, PNG, PDF 지원 (최대 10MB)</p>
                </div>
              )}

              {docStatus?.status === "pending" && (
                <div>
                  <div className="flex items-center gap-2 py-3 px-4 bg-amber-50 border border-amber-200 rounded-xl mb-3" data-testid="doc-status-pending">
                    <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span className="text-sm text-amber-700">서류 심사 중입니다. 관리자 승인을 기다려주세요.</span>
                  </div>
                  <input type="file" ref={docFileInputRef} accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleDocumentUpload} className="hidden" data-testid="doc-file-input-repending" />
                  <button onClick={() => docFileInputRef.current?.click()} disabled={isUploadingDoc}
                    className="w-full flex items-center justify-center gap-2 py-2.5 border border-stone-300 rounded-xl text-stone-500 hover:border-cyan-400 hover:text-cyan-600 transition-all text-sm disabled:opacity-50"
                    data-testid="doc-repending-btn">
                    {isUploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    서류 다시 첨부하기
                  </button>
                  <p className="text-xs text-stone-400 mt-2 text-center">잘못 올렸거나 파일에 문제가 있으면 다시 첨부해 교체할 수 있어요</p>
                </div>
              )}

              {docStatus?.status === "approved" && (
                <div className="flex items-center gap-2 py-3 px-4 bg-green-50 border border-green-200 rounded-xl" data-testid="doc-status-approved">
                  <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-700 font-medium">서류인증 완료 (10,000P 지급 완료)</span>
                </div>
              )}

              {docStatus?.status === "rejected" && (
                <div>
                  <div className="flex items-center gap-2 py-3 px-4 bg-red-50 border border-red-200 rounded-xl mb-3" data-testid="doc-status-rejected">
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <div>
                      <span className="text-sm text-red-700">서류가 반려되었습니다.</span>
                      {docStatus.review_note && <p className="text-xs text-red-500 mt-0.5">사유: {docStatus.review_note}</p>}
                    </div>
                  </div>
                  <input type="file" ref={docFileInputRef} accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleDocumentUpload} className="hidden" data-testid="doc-file-input-retry" />
                  <button onClick={() => docFileInputRef.current?.click()} disabled={isUploadingDoc}
                    className="w-full flex items-center justify-center gap-2 py-2.5 border border-stone-300 rounded-xl text-stone-500 hover:border-cyan-400 hover:text-cyan-600 transition-all text-sm disabled:opacity-50"
                    data-testid="doc-retry-btn">
                    {isUploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    다시 제출하기
                  </button>
                </div>
              )}
            </div>
            <div className="mt-6 p-4 bg-stone-50 rounded-xl">
              <p className="text-sm text-stone-500 mb-2">자기소개</p>
              {isEditing ? (
                <textarea
                  value={formData.introduction}
                  onChange={(e) => setFormData({ ...formData, introduction: e.target.value })}
                  className="w-full px-3 py-2 bg-white rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 min-h-[100px] resize-none"
                  placeholder="자기소개를 입력하세요"
                  data-testid="edit-introduction"
                />
              ) : (
                <p className="text-stone-700 leading-relaxed">
                  {user.introduction || "아직 자기소개가 없습니다."}
                </p>
              )}
            </div>

            {/* Member Info */}
            <div className="mt-6 pt-6 border-t border-stone-200">
              <p className="text-sm text-stone-400">
                가입일: {new Date(user.created_at).toLocaleDateString("ko-KR")}
              </p>
            </div>

            {/* 계정 보안 — 비밀번호 변경 */}
            <div className="mt-4 p-5 bg-stone-50 rounded-xl border border-stone-100" data-testid="password-section">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <KeyRound className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800 text-sm">비밀번호 변경</p>
                    <p className="text-xs text-stone-400">계정 보안을 위해 주기적으로 변경해 주세요</p>
                  </div>
                </div>
                {!showPwForm && (
                  <Button
                    variant="outline"
                    onClick={() => { setShowPwForm(true); setPwError(""); setPwSuccess(""); }}
                    className="border-stone-300 text-stone-600 text-sm"
                    data-testid="open-password-form-btn"
                  >
                    변경
                  </Button>
                )}
              </div>

              {showPwForm && (
                <form onSubmit={handleChangePassword} className="mt-4 space-y-3" data-testid="password-form">
                  {pwError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="password-error">
                      {pwError}
                    </div>
                  )}
                  {pwSuccess && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm" data-testid="password-success">
                      {pwSuccess}
                    </div>
                  )}
                  {[
                    { key: "current", label: "현재 비밀번호", testid: "current-password-input" },
                    { key: "next", label: "새 비밀번호 (6자 이상)", testid: "new-password-input" },
                    { key: "confirm", label: "새 비밀번호 확인", testid: "confirm-password-input" },
                  ].map(({ key, label, testid }) => (
                    <div key={key} className="relative">
                      <input
                        type={pwShow[key] ? "text" : "password"}
                        value={pwForm[key]}
                        onChange={(e) => setPwForm({ ...pwForm, [key]: e.target.value })}
                        placeholder={label}
                        className="w-full px-3 py-2.5 pr-10 bg-white rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 text-sm"
                        data-testid={testid}
                        autoComplete={key === "current" ? "current-password" : "new-password"}
                      />
                      <button
                        type="button"
                        onClick={() => setPwShow({ ...pwShow, [key]: !pwShow[key] })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                        tabIndex={-1}
                      >
                        {pwShow[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setShowPwForm(false); setPwForm({ current: "", next: "", confirm: "" }); setPwError(""); }}
                      className="border-stone-300 text-stone-600 text-sm"
                      data-testid="cancel-password-btn"
                    >
                      취소
                    </Button>
                    <Button
                      type="submit"
                      disabled={pwLoading}
                      className="btn-primary flex items-center gap-2 text-sm"
                      data-testid="submit-password-btn"
                    >
                      {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      비밀번호 변경
                    </Button>
                  </div>
                </form>
              )}
            </div>

            {/* 회원정보보기 노출 토글 - 모든 사용자에게 표시 */}
            <div className={`mt-4 p-4 rounded-xl border ${user.hidden_from_listing ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  {user.hidden_from_listing ? (
                    <EyeOff className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  ) : (
                    <Eye className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                  )}
                  <div>
                    <p className={`text-sm font-semibold ${user.hidden_from_listing ? "text-amber-800" : "text-emerald-800"}`}>
                      회원정보보기 노출 {user.hidden_from_listing ? "OFF" : "ON"}
                    </p>
                    <p className={`text-xs mt-0.5 ${user.hidden_from_listing ? "text-amber-700" : "text-emerald-700"}`}>
                      {user.hidden_from_listing
                        ? "지금은 다른 회원에게 프로필이 보이지 않아요"
                        : "다른 회원들이 회원정보보기에서 나를 찾을 수 있어요"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!user.hidden_from_listing}
                  disabled={listingBusy}
                  onClick={() => handleToggleListing(!user.hidden_from_listing)}
                  data-testid="profile-listing-toggle"
                  className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${user.hidden_from_listing ? "bg-stone-300" : "bg-emerald-500"}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${user.hidden_from_listing ? "translate-x-1" : "translate-x-6"}`}
                  />
                </button>
              </div>
            </div>

            {/* 멤버십 캔슬 & 업그레이드 버튼 */}
            <div className="mt-4 flex items-center gap-4">
              <button
                onClick={() => setShowCancelModal(true)}
                className="text-sm text-red-400 hover:text-red-500 transition-colors flex items-center gap-1.5"
                data-testid="cancel-membership-btn"
              >
                <XCircle className="w-4 h-4" />
                멤버십 캔슬하기
              </button>
              <button
                onClick={() => navigate("/membership")}
                className="text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors flex items-center gap-1.5"
                data-testid="upgrade-membership-btn"
              >
                <Award className="w-4 h-4" />
                멤버십 업그레이드 하기
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* 멤버십 캔슬 확인 팝업 */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCancelModal(false)} data-testid="cancel-modal-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="cancel-modal">
            <div className="bg-red-500 p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-white font-bold text-lg">멤버십 캔슬</h3>
            </div>
            <div className="p-6">
              <p className="text-stone-800 text-center font-medium mb-2">
                어떤 방식으로 캔슬하시겠습니까?
              </p>
              <p className="text-stone-400 text-xs text-center mb-5">
                환불은 적용되지 않습니다.
              </p>

              <div className="space-y-3 mb-5">
                {/* 옵션 1: 데이터 삭제 + 멤버십 캔슬 */}
                <button
                  onClick={() => requestConfirm(true)}
                  disabled={isCancelling}
                  className="w-full text-left border-2 border-red-200 hover:border-red-400 bg-red-50 hover:bg-red-100 rounded-xl p-4 transition-all"
                  data-testid="cancel-with-delete"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-red-200 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-red-700 text-sm">모든 데이터 삭제 후 캔슬</p>
                      <p className="text-red-500 text-xs mt-1 leading-relaxed">
                        작성한 모든 게시글, 연애사관학교 이수학점,<br />
                        내 프로필 상세 정보(자기소개서)가 모두 삭제됩니다.
                      </p>
                    </div>
                  </div>
                </button>

                {/* 옵션 2: 데이터 유지, 회원 정보보기에서만 삭제 */}
                <button
                  onClick={() => requestConfirm(false)}
                  disabled={isCancelling}
                  className="w-full text-left border-2 border-blue-200 hover:border-blue-400 bg-blue-50 hover:bg-blue-100 rounded-xl p-4 transition-all"
                  data-testid="cancel-hide-listing"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <EyeOff className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-blue-700 text-sm">데이터 유지, 회원 정보보기에서만 삭제</p>
                      <p className="text-blue-600 text-xs mt-1 leading-relaxed">
                        기존 게시글, 이수학점, 프로필 정보는 그대로 유지되며<br />
                        회원 정보보기 목록에서만 비공개 처리됩니다.
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {isCancelling && (
                <div className="flex justify-center mb-4">
                  <Loader2 className="w-5 h-5 animate-spin text-red-500" />
                </div>
              )}

              <Button
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
                className="w-full bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-xl py-3"
                data-testid="cancel-modal-close"
              >
                취소
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 확인 팝업 */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="confirm-popup">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl">
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-7 h-7 text-red-500" />
              </div>
              <p className="text-stone-800 font-semibold mb-2">정말 진행하시겠습니까?</p>
              <p className="text-stone-500 text-sm leading-relaxed">{confirmMessage}</p>
            </div>
            <div className="flex border-t border-stone-100">
              <button
                onClick={() => setConfirmAction(null)}
                disabled={isCancelling}
                className="flex-1 py-4 text-stone-500 font-medium hover:bg-stone-50 transition-colors text-sm"
                data-testid="confirm-cancel-btn"
              >
                아니오
              </button>
              <button
                onClick={confirmAction}
                disabled={isCancelling}
                className="flex-1 py-4 text-red-500 font-semibold hover:bg-red-50 transition-colors border-l border-stone-100 text-sm"
                data-testid="confirm-yes-btn"
              >
                {isCancelling ? "처리 중..." : "네, 진행합니다"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
